import React from 'react';

import VideoEmbed from '../UseCaseComponents/VideoEmbed';
import PdfEmbed from '../UseCaseComponents/PdfEmbed';
import ImageSection from '../UseCaseComponents/ImageSection';
import TextSection from '../UseCaseComponents/TextSection';
import DynamicPricingOnePager from '../UseCaseComponents/DynamicPricingOnePager';
import AgenticReadinessOnePager from '../UseCaseComponents/AgenticReadinessOnePager';
import PredictiveRestockOnePager from '../UseCaseComponents/PredictiveRestockOnePager';
import PersonalizedContentDiscoveryOnePager from '../UseCaseComponents/PersonalizedContentDiscoveryOnePager';
import AmazonSellerIntelligenceOnePager from '../UseCaseComponents/AmazonSellerIntelligenceOnePager';

// Registry of one-off "rich content" components -- a hand-built responsive
// HTML re-creation of a specific PDF one-pager (see richContent below),
// keyed by name so useCases.js can reference one by string, same pattern
// as UseCaseView's `layouts` map.
const richContentComponents = {
  DynamicPricingOnePager,
  AgenticReadinessOnePager,
  PredictiveRestockOnePager,
  PersonalizedContentDiscoveryOnePager,
  AmazonSellerIntelligenceOnePager,
};

// For a use case that's a recorded walkthrough (e.g. a Flow/App demo video,
// since those can't be live-embedded -- see SHARED-CHAT-EMBEDDING notes)
// plus a report. Video up top, then the text explanation, then the report.
//
// Three ways to show the report, pick one per use case in useCases.js:
//   - `pdf: { url, heading, height }` -- a multi-page PDF in a contained,
//     scrollable box (PdfEmbed). Good when the document is long and you
//     don't want it taking over the page.
//   - `pageImage: { src, alt, heading }` -- a single page rendered as an
//     actual image (a plain <img>, same as the video use case's `photo`).
//     Displays fully, full-width, with no scrolling, no PDF-viewer
//     toolbar, and no letterboxing -- good for a one-pager, EXCEPT on
//     small screens an image just shrinks and its text becomes
//     unreadable, since it can't reflow.
//   - `richContent: 'ComponentName'` -- the one-pager rebuilt as real,
//     responsive HTML (see richContentComponents above) so its text wraps
//     and stays legible at any screen width, phones included. This is the
//     right choice over pageImage specifically because it's readable on
//     mobile; it does mean hand-building a component with the document's
//     actual text/structure rather than just dropping in a file.
const VideoReportStarterLayout = ({ useCase }) => {
  const { video, pdf, pageImage, richContent, text } = useCase.components;
  const RichContent = richContent ? richContentComponents[richContent] : null;

  // When there's a `richContent` component, IT places the video itself
  // (see DynamicPricingOnePager) -- alongside its own intro copy in a
  // two-column layout, instead of a full-width video stacked above
  // everything. Same deal when `heroBanner` is on (see useCases.js /
  // UseCaseView): the video already shows up top on the gradient hero
  // band, so rendering it again here would just duplicate it. Either way,
  // the intro `text` block still renders normally below -- only the video
  // itself is the one being skipped.
  return (
    <div className="mt-8 w-full">
      {!RichContent && !useCase.heroBanner && (
        <VideoEmbed
          url={video?.url}
          title={useCase.title}
          className="w-full rounded-xl"
        />
      )}

      {!RichContent && (text?.heading || text?.body) && (
        <TextSection
          heading={text?.heading}
          body={text?.body}
          className="mt-8 w-full"
        />
      )}

      {pdf?.url && (
        <div className="mt-8">
          <h3 className="mb-3 text-lg font-semibold text-gray-900">
            {pdf?.heading || 'Full report'}
          </h3>

          <PdfEmbed
            url={pdf.url}
            title={pdf?.title || `${useCase.title} report`}
            height={pdf?.height}
          />
        </div>
      )}

      {pageImage?.src && (
        // Capped to just under half the page width on larger screens (full
        // width on mobile, since a phone screen is already narrow enough).
        <div className="mt-8 w-full sm:w-[45%]">
          {pageImage?.heading && (
            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              {pageImage.heading}
            </h3>
          )}

          <ImageSection
            src={pageImage.src}
            alt={pageImage?.alt || `${useCase.title} one-pager`}
            className="w-full rounded-xl"
          />
        </div>
      )}

      {RichContent && (
        <div className="mt-8 w-full">
          <RichContent video={video} useCaseTitle={useCase.title} />
        </div>
      )}
    </div>
  );
};

export default VideoReportStarterLayout;
